import { useEffect, useRef, useState } from "react";
import { Camera, Sparkles, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const Slot = ({ label, hint, file, existingUrl, onPick, onClear, testid }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const shown = preview || existingUrl;

  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-moss">{label}</p>
      <div className="mt-1.5 relative overflow-hidden rounded-lg border border-[#E8E3D9] bg-white">
        {shown ? (
          <>
            <img src={shown} alt={label} className="h-28 w-full object-cover" />
            <Button
              type="button"
              data-testid={`${testid}-clear`}
              onClick={onClear}
              className="absolute right-1.5 top-1.5 h-7 w-7 rounded-full bg-deep-olive/70 p-0 text-sand hover:bg-deep-olive transition-colors duration-200"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </>
        ) : (
          <button
            type="button"
            data-testid={testid}
            onClick={() => inputRef.current?.click()}
            className="flex h-28 w-full flex-col items-start justify-center gap-1 px-4 text-left hover:bg-surface transition-colors duration-200"
          >
            <Camera className="h-5 w-5 text-moss" />
            <span className="text-sm text-stone-grey">{hint}</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
      />
    </div>
  );
};

export const PlateAnalyzer = ({ photos, setPhotos, onResult }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyze = async () => {
    if (!photos.beforeFile && !photos.photo_url) {
      toast.error("Add a before photo of the plate first");
      return;
    }
    if (!photos.beforeFile) {
      toast.error("Re-upload the before photo to run a new analysis");
      return;
    }
    const body = new FormData();
    body.append("before_image", photos.beforeFile);
    if (photos.afterFile) body.append("after_image", photos.afterFile);

    setLoading(true);
    try {
      const { data } = await api.post("/ai/analyze-plate", body);
      setResult(data);
      onResult(data);
      setPhotos((p) => ({
        ...p,
        photo_url: data.photo_url,
        after_photo_url: data.after_photo_url || p.after_photo_url,
      }));
      toast.success(`AI: ${Math.round(data.eaten_calories)} kcal actually eaten`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI could not read the photos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#E8E3D9] bg-surface p-4" data-testid="plate-analyzer">
      <div className="flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-ochre" />
        <p className="font-heading text-sm font-medium text-deep-olive">
          Before &amp; after photo compare
        </p>
      </div>
      <p className="mt-1 text-xs text-stone-grey">
        GPT-5.5 reads the served plate, then the leftovers, and works out what you actually ate.
      </p>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <Slot
          testid="before-photo-slot"
          label="Before"
          hint="Full plate"
          file={photos.beforeFile}
          existingUrl={photos.photo_url}
          onPick={(f) => setPhotos((p) => ({ ...p, beforeFile: f }))}
          onClear={() => setPhotos((p) => ({ ...p, beforeFile: null, photo_url: null }))}
        />
        <Slot
          testid="after-photo-slot"
          label="After"
          hint="Leftovers"
          file={photos.afterFile}
          existingUrl={photos.after_photo_url}
          onPick={(f) => setPhotos((p) => ({ ...p, afterFile: f }))}
          onClear={() => setPhotos((p) => ({ ...p, afterFile: null, after_photo_url: null }))}
        />
      </div>

      <Button
        type="button"
        data-testid="analyze-plate-button"
        onClick={analyze}
        disabled={loading}
        className="mt-3 h-10 w-full rounded-full bg-deep-olive text-sand hover:bg-forest transition-colors duration-200"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Reading your plate…
          </>
        ) : (
          <>
            <Sparkles className="mr-2 h-4 w-4" /> Analyse with AI
          </>
        )}
      </Button>

      {result && (
        <div
          className="mt-3 rounded-lg bg-[#EAE5D9] px-4 py-3 text-sm text-deep-olive"
          data-testid="ai-result"
        >
          <p className="font-heading font-medium">
            {result.name} · {Math.round(result.eaten_calories)} kcal eaten
          </p>
          <p className="mt-0.5 text-xs text-stone-grey">
            {Math.round(result.calories)} kcal served · {Math.round(result.portion_after)}% left ·
            confidence {result.confidence}
          </p>
          {result.note && <p className="mt-1 text-xs text-stone-grey">{result.note}</p>}
        </div>
      )}
    </div>
  );
};
